// cookie-bridge.js — manages @capgo InAppBrowser lifecycle for book SlipSync
// Spec: docs/specs/slipsync-ui.md Sections 4, 6, 9; slipsync-adapter-framework.md §5
// Book-neutral: WebView URL, injected script, and title come from the passed BookSyncConfig.
// For a capture-stage config (#885, ADR 006) the bridge injects nothing after load: the
// book's documentStart intercept posts capture_page messages, the bridge collects them,
// and closing the browser means "done".

import { InAppBrowser } from '@capgo/capacitor-inappbrowser';
import * as Sentry from '../errorReporting';
import { parseBetslipsMessage, parseCapturePage } from './betmgm-client';
import { randomUUID } from '../platform';
import { ROUTE_SEGMENTS } from './attempt-exit-map';

// Bridge hardening (spec §6, §9; Ultron revision): a sign-in takes redirects and a 2FA step. Nothing closes the window
// except bets, a cancel, a definite book error after sign-in, or inactivity (3 min without a page change; 9.5 min cap,
// which keeps the ledger duration under its 600000 CHECK). Requests are bounded, never a close reason: spaced 3 s, a
// same-URL repeat within 15 s skipped, back-off on repeated not-signed-in for one URL, 30 per window then page-load-only.
export const INACTIVITY_MS = 180000;
export const HARD_CAP_MS = 570000;
export const REQUEST_SPACING_MS = 3000;
export const SAME_URL_SKIP_MS = 15000;
export const MAX_INJECTIONS_PER_WINDOW = 30;
const BACKOFF_MS = [3000, 6000, 12000, 15000];
// Route names share the trail (2026-10-18), so it holds more than the original 20 notes.
const MAX_TRAIL = 40;
const pathOf = (u) => { try { return new URL(u).pathname; } catch { return ''; } }; // '' before the first urlChange (expected control flow)
// Sign-in-page skip (spec §9 rule 1a): no request while any path segment is a sign-in or verify route, so a
// sign-in and its 2FA step send the book nothing from us (the 2026-10-12 BetMGM lockout). Same list as the BetMGM
// script's AUTH_ROUTES (pinned by a test). Exact segment, any depth (BetMGM's login has lived at /en/labelhost/login).
export const SIGNIN_SEGMENTS = new Set(['login', 'signin', 'sign-in', 'auth', 'verify', 'verification']);
const isSignInPage = (u) => pathOf(u).toLowerCase().split('/').some((s) => SIGNIN_SEGMENTS.has(s));
// The bridge's check reads the last delivered urlChangeEvent, which can trail the WebView: a request coming due just
// after a navigation to /login would pass it. So the injected code re-checks the LIVE location.pathname, with the
// same rule, and returns before the book script runs (Codex P1, #1487). Silent: no message, the window waits.
// #1409: the same wrapper refuses to run anywhere but the book's own hosts (config.scriptHosts, exact or a
// subdomain, over https: only), so a page the WebView was redirected to never sees the script or its per-open nonce.
// The host check runs in the page's realm, so it calls nothing the page can replace (local Codex P1: an overridden
// String/toLowerCase/slice made a foreign host read as the book's): only the unforgeable location getters, ===,
// typeof, .length and string indexing. location.hostname is already lowercase for an https URL. It runs before the
// sign-in check, whose string methods then decide only an early return. A config with no host list gets no request
// at all (fail closed). Silent, like the sign-in skip. Residual by design (same as capture mode): a script already
// running in the book's own main frame can read the nonce.
const HOST_GUARD = 'var L=location,h=L.hostname,ok=false;'
  + 'if(L.protocol==="https:"&&typeof h==="string"){for(var j=0;j<a.length;j++){var d=a[j],n=h.length,m=d.length;'
  + 'if(h===d){ok=true;break;}'
  + 'if(n>m+1&&h[n-m-1]==="."){var same=true;for(var k=0;k<m;k++){if(h[n-m+k]!==d[k]){same=false;break;}}'
  + 'if(same){ok=true;break;}}}}'
  + 'if(!ok)return;';
const withLiveSignInGuard = (script, hosts) => '(function(){'
  + `var a=${JSON.stringify(Array.isArray(hosts) ? hosts.filter((x) => typeof x === 'string' && x) : [])};`
  + HOST_GUARD
  + 'var p=String(location.pathname||"").toLowerCase().split("/");'
  + `var s=${JSON.stringify(Array.from(SIGNIN_SEGMENTS))};`
  + 'for(var i=0;i<p.length;i++){if(s.indexOf(p[i])!==-1)return;}\n;' + script + '\n})();';
// #1409 (local Codex P1): the bridge also skips a request while the last delivered URL is on another host. That URL
// can trail the WebView, so this only saves the request (nothing is counted); the in-page host check above is the
// authoritative one. An unknown URL ('' before the first urlChange) is left to the in-page check.
const hostOf = (u) => { try { return new URL(u).hostname.toLowerCase(); } catch { return ''; } }; // '' = unknown (expected control flow)
const isOffHost = (u, hosts) => {
  const h = hostOf(u);
  if (!h) return false;
  return !(Array.isArray(hosts) ? hosts : []).some((d) => h === d || h.endsWith('.' + d));
};
// #1409: the diagnostics name an unbound message's type only when it is a protocol type; page text never reaches them.
const UNBOUND_TYPES = new Set(['betslips', 'partial', 'auth_error', 'api_error', 'script_error']);
const URL_CHANGE_DELAY_MS = 2000;
// #885 capture stage caps (spec §4 step 4).
const MAX_CAPTURE_PAGES = 10;
const MAX_CAPTURE_CHARS = 300000; // < 512 KB route cap after JSON escaping (plan self-review)
const CAPTURE_TIMEOUT_MS = 600000; // the user browses My Bets themselves (Ultron r1 A3)
// A load error with no later finished load closes the browser (A3). 30 s, and an
// authenticated capture_ready cancels a timer armed before it: the plugin's documentStart
// reload supersedes the first navigation, which reports pageLoadError on every open
// (pre-Codex FC-I1, plugin 8.6.34 WKWebViewController.swift:2009-2013, 2927-2934). A later
// error before didFinish re-arms it: with isPresentAfterPageLoad the browser is still hidden,
// so the user has no close button (Nova P2-1).
const PAGE_LOAD_GRACE_MS = 30000;
const MAX_DROPPED = 1000; // server meta contract

// Origin + FIRST path segment, for Sentry safety -- and only when that segment is a known
// route name (allowlist). It shows where a book redirected the WebView (/login vs /mybets --
// 2026-10-01 DK tester log); any other segment can be a token, email or account id
// (padded Base64, percent-encoded, short ids all slip past a pattern) -> ':redacted'
// (Codex P1, PR #1293). Deeper segments, query and fragment are never logged.
// Non-http(s) URLs (data:, javascript:, blob:) log the scheme only -- their "path" is content.
// The list is ROUTE_SEGMENTS (attempt-exit-map.js, also the PostHog trail enum) plus the locale segment.
const SAFE_PATH_SEGMENTS = new Set(['en', ...ROUTE_SEGMENTS]);

// Where the WebView is, for the trail (2026-10-18): the DEEPEST known route segment of the path ('en' skipped), so
// /en/sports/my-bets is 'my-bets' and /en/labelhost/login is 'login'. No segment beyond the locale is 'root'; only
// unknown segments, or a non-http(s) URL, is 'other'. Never the segment text itself unless it is on the list.
function routeName(raw) {
  let u;
  try { u = new URL(raw); } catch { return 'other'; } // unparseable: no route (expected control flow)
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'other';
  const segs = u.pathname.toLowerCase().split('/').filter((x) => x && x !== 'en');
  if (segs.length === 0) return 'root';
  let name = 'other';
  for (const x of segs) if (ROUTE_SEGMENTS.includes(x)) name = x;
  return name;
}

function redactUrl(raw) {
  try {
    const u = new URL(raw);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return u.protocol;
    const first = u.pathname.split('/')[1] || '';
    if (!first) return u.origin;
    return `${u.origin}/${SAFE_PATH_SEGMENTS.has(first.toLowerCase()) ? first.toLowerCase() : ':redacted'}`;
  } catch { return '(invalid)'; }
}

const MAX_DIAG_ENTRIES = 50;
// #1409 pre-Codex Sec P2: a page can post unbound messages without limit; only the first few are logged (the 'done'
// entry carries the total), so a flood cannot fill the diagnostics and push out inject/timeout/done.
const MAX_UNBOUND_LOGGED = 3;

// Opens the book's WebView, injects its fetch script on page load or URL change,
// returns result. Registers its listeners BEFORE openWebView (race prevention): 4 in import
// mode, 5 in capture mode (adds pageLoadError).
// Returns { promise, cancel }. cancel() aborts the pending bridge, closes
// WebView, and resolves the promise with { type: 'cancelled' }.
// Every result includes a `diagnostics` array for observability.
// `config` is a BookSyncConfig (getWebViewUrl, getFetchScript, scriptHosts, webViewTitle; capture books:
// getCaptureScript);
// `region` is the book-specific region/state string.
export function openBookWebView(config, region) {
  let scriptAttempts = 0; // diagnostics only (bridge hardening: no attempt cap)
  const trail = ['open'];
  // A run of the same note (e.g. 30 not-signed-in answers) takes one slot, so later notes such as
  // inject_ceiling are not pushed out of the bounded trail (Nova P3-1).
  const note = (t) => {
    if (trail[trail.length - 1] === t) return;
    if (trail.length < MAX_TRAIL) trail.push(t); else trail[MAX_TRAIL - 1] = t;
  };
  let closing = false; // set synchronously on every terminal path: no timer or late event acts after it (P2-5)
  let lastInjectAt = -Infinity;
  let spacingTimerId = null;
  let inactivityId = null;
  let hardCapId = null;
  let injections = 0;
  let ceilingNoted = false;
  let currentUrl = '';
  let lastPath = '';
  let lastRoute = '';
  // 2026-10-18 visibility: the book's LAST not-signed-in answer, returned with every resolution.
  let lastAnswer = null;
  // config.definitiveNoBetsReasons close at the first answer, and only on the bet-history route
  // (config.betHistorySegments; BetMGM only). No other not-signed-in answer ever closes the window.
  const historySegments = new Set(Array.isArray(config.betHistorySegments) ? config.betHistorySegments : []);
  const definitiveReasons = Array.isArray(config.definitiveNoBetsReasons) ? config.definitiveNoBetsReasons : [];
  // Before the first urlChangeEvent the WebView is on the URL it opened (BetMGM: My Bets). A redirect to sign-in
  // before that event sends no answer: the injected live guard silences the script on a sign-in path.
  let openedUrl = '';
  const onBetHistory = () => pathOf(currentUrl || openedUrl).toLowerCase().split('/').some((x) => historySegments.has(x));
  const noteRoute = (url) => {
    const r = routeName(url);
    if (r !== lastRoute) { lastRoute = r; note(`route:${r}`); }
  };
  let notSignedUrl = ''; // URL of the last not-signed-in answer
  let notSignedAt = 0;
  let notSignedStreak = 0; // consecutive not-signed-in answers for notSignedUrl
  // A swallowed script_error is waiting on the scheduled re-inject: later urlChanges must
  // not push that timer back, or a navigation storm turns a fast failure into a 180s timeout.
  let scriptErrorRetryPending = false;
  let timeoutId = null;
  let urlChangeTimerId = null;
  const handles = [];
  let onTimeout = () => {};
  // Activity (import mode): every page change re-arms the inactivity timer.
  const touch = () => {
    if (resolved || closing || isCaptureMode()) return;
    if (inactivityId) clearTimeout(inactivityId);
    inactivityId = setTimeout(() => onTimeout(), INACTIVITY_MS);
  };
  const diagnostics = [];
  const t0 = Date.now();

  // #885 (ADR 006): a capture-stage book records the page's own responses (documentStart
  // intercept) and never gets an executeScript; closing the browser means "done".
  const isCapture = config.stage === 'capture';
  function isCaptureMode() { return isCapture; }
  const capturedPages = [];
  const capturedHeaderNames = new Set();
  let capturedChars = 0;
  let droppedCount = 0;
  let anyTruncated = false;
  let lastHost = '';
  let installed = false;
  let pageLoaded = false;
  let graceTimer = null;
  let cancelling = false;
  // Per-open nonce baked into the injected script: a message without it came from a subframe or another document
  // posting to the native handler, and is dropped (pre-Codex Sec-1 for capture; #1409 for import books).
  const nonce = randomUUID();
  let unboundCount = 0;
  // #1587: coded counters for the attempt's PostHog event (uncapped, unlike diagnostics). No page text.
  let authErrors = 0;
  let lastAuthReason = null;
  let lastSkip = null;
  const capturedDetail = () => ({
    type: 'captured',
    pages: capturedPages.slice(),
    meta: {
      pageCount: capturedPages.length,
      droppedCount: Math.min(droppedCount, MAX_DROPPED),
      truncated: anyTruncated || droppedCount > 0,
      host: lastHost,
      // A clock step backwards must not 400 the capture (Ultron delta G10).
      durationMs: Math.max(0, Math.min(Date.now() - t0, 600000)),
      capturedHeaderNames: Array.from(capturedHeaderNames).slice(0, 30),
      installed,
    },
  });

  const skip = (trigger, reason) => { lastSkip = reason; log('inject_skip', { trigger, reason }); };
  const log = (event, extra) => {
    if (diagnostics.length >= MAX_DIAG_ENTRIES) return;
    diagnostics.push({ t: Date.now() - t0, event, attempts: scriptAttempts, ...extra });
  };

  const cleanup = async () => {
    if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
    if (urlChangeTimerId) { clearTimeout(urlChangeTimerId); urlChangeTimerId = null; }
    if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
    if (spacingTimerId) { clearTimeout(spacingTimerId); spacingTimerId = null; }
    if (inactivityId) { clearTimeout(inactivityId); inactivityId = null; }
    if (hardCapId) { clearTimeout(hardCapId); hardCapId = null; }
    for (const h of handles) {
      try { await h.remove(); } catch (_) { /* already removed */ }
    }
    handles.length = 0;
    try { await InAppBrowser.removeAllListeners(); } catch (_) {}
  };

  let resolved = false;
  let resolveFn;
  const done = (result) => {
    if (resolved) return;
    resolved = true;
    closing = true;
    log('done', { resultType: result.type, ...(unboundCount ? { unbound: unboundCount } : {}) });
    resolveFn({
      ...result, diagnostics, trail: trail.slice(),
      lastAnswer: lastAnswer ? { ...lastAnswer, keys: lastAnswer.keys ? lastAnswer.keys.slice() : null } : null,
      stats: { injects: injections, authErrors, unbound: unboundCount, lastAuthReason, lastSkip },
    });
  };

  const cancel = async () => {
    if (resolved) return;
    closing = true;
    note('cancelled');
    cancelling = true; // close() can fire closeEvent synchronously (A3)
    try { await InAppBrowser.close(); } catch (_) {}
    await cleanup();
    done({ type: 'cancelled' });
  };

  const promise = new Promise((resolve) => {
    resolveFn = resolve;

    const registerAndOpen = async () => {
      try {
        const closeHandle = await InAppBrowser.addListener('closeEvent', async () => {
          if (isCapture && cancelling) return;
          if (!isCapture && (resolved || closing)) return;
          closing = true;
          note('closed');
          log('closeEvent');
          await cleanup();
          done(isCapture ? { type: 'message', detail: capturedDetail() } : { type: 'close' });
        });
        handles.push(closeHandle);

        const injectScript = async (trigger) => {
          if (resolved || closing) return;
          // Checked when the request comes due, so a deferred one that lands on a sign-in page is dropped too. The
          // next page change brings the request back; nothing is counted, so skips never reach the ceiling.
          if (isSignInPage(currentUrl)) {
            scriptErrorRetryPending = false;
            note('signin_page');
            skip(trigger, 'signin_page');
            return;
          }
          if (isOffHost(currentUrl, config.scriptHosts)) {
            scriptErrorRetryPending = false;
            skip(trigger, 'off_host');
            return;
          }
          if (injections >= MAX_INJECTIONS_PER_WINDOW) {
            if (!ceilingNoted) { note('inject_ceiling'); ceilingNoted = true; }
            const p = pathOf(currentUrl);
            if (trigger !== 'pageLoad' || !p || p === lastPath) { skip(trigger, 'ceiling'); return; }
          }
          const sameUrl = !!currentUrl && currentUrl === notSignedUrl;
          if (trigger === 'urlChange' && sameUrl && Date.now() - notSignedAt < SAME_URL_SKIP_MS) {
            skip(trigger, 'same_url');
            return;
          }
          const gap = sameUrl ? (BACKOFF_MS[Math.min(notSignedStreak - 1, BACKOFF_MS.length - 1)] || REQUEST_SPACING_MS) : REQUEST_SPACING_MS;
          const since = Date.now() - lastInjectAt;
          if (since < gap) {
            if (!spacingTimerId) {
              spacingTimerId = setTimeout(() => { spacingTimerId = null; scriptErrorRetryPending = false; injectScript(trigger); }, gap - since);
            }
            return;
          }
          lastInjectAt = Date.now();
          injections++;
          lastPath = pathOf(currentUrl) || lastPath;
          scriptAttempts++;
          scriptErrorRetryPending = false;
          log('inject', { trigger });
          try {
            const script = withLiveSignInGuard(config.getFetchScript(region, nonce), config.scriptHosts);
            await InAppBrowser.executeScript({ code: script });
          } catch (e) {
            const errMsg = String(e.message || e).replace(/[^\x20-\x7E]/g, '').slice(0, 200);
            log('inject_error', { trigger, error: errMsg });
            Sentry.captureMessage(`SlipSync executeScript failed: ${errMsg}`, 'warning');
            closing = true;
            // An exit carries only the answer that caused it: the inject failed, the book did not answer this one.
            lastAnswer = null;
            note('script_error');
            try { await closeHandle.remove(); } catch (_) {}
            try { await InAppBrowser.close(); } catch (_) {}
            await cleanup();
            done({ type: 'script_error', error: e.message || String(e) });
          }
        };

        if (!isCapture) {
          // browserPageLoaded: authoritative signal that page is ready.
          // Always injects. Cancels any pending urlChangeEvent injection.
          const pageLoadHandle = await InAppBrowser.addListener('browserPageLoaded', async () => {
            log('pageLoad');
            touch();
            if (urlChangeTimerId) { clearTimeout(urlChangeTimerId); urlChangeTimerId = null; }
            await injectScript('pageLoad');
          });
          handles.push(pageLoadHandle);

          // urlChangeEvent: fires on SPA navigation (and before browserPageLoaded
          // on full navigations). Uses a 2s delay so browserPageLoaded can cancel
          // it — browserPageLoaded is the reliable post-load signal.
          const urlChangeHandle = await InAppBrowser.addListener('urlChangeEvent', (event) => {
            const url = (event && event.url) || '';
            log('urlChange', { url: redactUrl(url) });
            if (resolved || closing) return;
            currentUrl = url;
            noteRoute(url);
            // A sign-in page sends no request, so it must clear the not-signed-in memory itself: a fast sign-in back to
            // the page that last answered would be a same-URL repeat and wait out the inactivity timeout (pre-Codex FC-1).
            // Here, not in the skip: leaving inside the 2 s debounce never reaches injectScript.
            if (isSignInPage(url)) notSignedUrl = '';
            touch();
            if (scriptErrorRetryPending && urlChangeTimerId) return; // let the retry inject fire
            if (urlChangeTimerId) clearTimeout(urlChangeTimerId);
            urlChangeTimerId = setTimeout(async () => {
              urlChangeTimerId = null;
              await injectScript('urlChange');
            }, URL_CHANGE_DELAY_MS);
          });
          handles.push(urlChangeHandle);
        } else {
          // Capture mode: track load state for the A3 grace timer and the page host for meta.
          // Nothing is injected after load -- the intercept was installed at documentStart.
          const failOpen = async () => {
            graceTimer = null;
            log('page_load_failed');
            try { await closeHandle.remove(); } catch (_) {}
            try { await InAppBrowser.close(); } catch (_) {}
            await cleanup();
            // Pages already captured from a committed document are still delivered (Nova P2-1).
            if (capturedPages.length > 0) { done({ type: 'message', detail: capturedDetail() }); return; }
            done({ type: 'open_error', error: 'page_load_failed' });
          };
          handles.push(await InAppBrowser.addListener('browserPageLoaded', () => {
            log('pageLoad');
            pageLoaded = true;
            if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
          }));
          handles.push(await InAppBrowser.addListener('pageLoadError', () => {
            log('pageLoadError');
            if (!pageLoaded && !graceTimer && !resolved) graceTimer = setTimeout(failOpen, PAGE_LOAD_GRACE_MS);
          }));
          handles.push(await InAppBrowser.addListener('urlChangeEvent', (event) => {
            const url = (event && event.url) || '';
            log('urlChange', { url: redactUrl(url) });
            if (!resolved) noteRoute(url);
            try {
              const u = new URL(url);
              if (u.protocol === 'https:' && /^[a-z0-9.-]{1,100}$/i.test(u.host)) lastHost = u.host.toLowerCase();
            } catch (_) { /* not a URL: keep the previous host */ }
          }));
        }

        const messageHandle = await InAppBrowser.addListener('messageFromWebview', async (event) => {
          if (isCapture) {
            if (resolved) return;
            const d = event && event.detail;
            if (d && (d.type === 'capture_ready' || d.type === 'capture_page') && d.nonce !== nonce) { log('capture_unbound'); return; }
            if (d && d.type === 'capture_ready') {
              installed = true;
              if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
              const pr = d.probe && typeof d.probe === 'object' ? d.probe : {};
              log('capture_ready', { probe: { b: typeof pr.b, n: pr.n === null ? 'null' : typeof pr.n, i: typeof pr.i, s: typeof pr.s } });
              return;
            }
            if (!d || d.type !== 'capture_page') { log('capture_ignore'); return; }
            const page = parseCapturePage(d.page, config.captureSpec);
            if (!page) { log('capture_reject'); return; }
            // Once a cap is hit, every later page is counted and dropped (spec §4 step 4).
            if (droppedCount > 0 || capturedPages.length >= MAX_CAPTURE_PAGES || capturedChars + page.body.length > MAX_CAPTURE_CHARS) {
              droppedCount++;
              log('capture_drop');
              return;
            }
            const { headerNames, ...stored } = page;
            headerNames.forEach((n) => capturedHeaderNames.add(n));
            capturedChars += stored.body.length;
            if (stored.truncated) anyTruncated = true;
            capturedPages.push(stored);
            log('capture_page', { status: stored.status });
            return;
          }
          const detail = event && event.detail;
          // #1409: a message without this open's nonce did not come from our script. It can neither save betslips
          // nor end the sign-in; logged (type only) and ignored.
          if (!detail || typeof detail !== 'object' || detail.nonce !== nonce) {
            unboundCount++;
            if (unboundCount <= MAX_UNBOUND_LOGGED) {
              log('message_unbound', { type: detail && UNBOUND_TYPES.has(detail.type) ? detail.type : 'other' });
            }
            return;
          }
          const parsed = parseBetslipsMessage(detail);
          log('message', { type: parsed.type, reason: parsed.reason, error: parsed.error });

          if (resolved || closing) return;
          // Not signed in yet (bridge hardening): never a close reason. Remember the URL so a same-URL repeat is
          // skipped and the next request backs off.
          if (parsed.type === 'auth_error') {
            authErrors++;
            lastAuthReason = parsed.reason || 'unknown';
            if (currentUrl === notSignedUrl) notSignedStreak++; else { notSignedUrl = currentUrl; notSignedStreak = 1; }
            notSignedAt = Date.now();
            lastAnswer = { reason: parsed.reason || 'unknown', status: parsed.status, ctype: parsed.ctype, keys: parsed.keys };
            note('not_signed_in:' + (parsed.reason || 'unknown'));
            log('message_retry', { type: parsed.type, reason: parsed.reason, status: parsed.status });
            // A definitive answer (BetMGM access_denied: /Account/AccessDenied, where it sends a blocked query) closes
            // at once, but only with bet-history evidence from BOTH the bridge's URL and the answer's own live route,
            // stamped in the page when it was posted (GitHub Codex P1s 8dfc1841, 02b25c3f): currentUrl can trail the
            // WebView, so a sign-in/2FA move mid-request never closes. Every other answer waits (GitHub Codex P1,
            // eda514bb): BetMGM answers 5xx, error bodies and redirects before sign-in too, so none proves a session.
            const liveHistory = parsed.route === 'history';
            const definitive = definitiveReasons.includes(parsed.reason) && onBetHistory() && liveHistory;
            if (!definitive) return;
            closing = true;
            note('signed_in_no_bets');
            try { await closeHandle.remove(); } catch (_) { /* already removed: expected control flow */ }
            try { await InAppBrowser.close(); } catch (_) { /* already closed: expected control flow */ }
            await cleanup();
            done({ type: 'book_no_bets' });
            return;
          }
          // Retryable: script_error while a navigation re-inject is already scheduled. The
          // page navigated under the script (2026-10-01 DK tester: urlChange at 2716ms,
          // script_error at 2731ms) and the pending urlChange timer re-injects. With nothing
          // scheduled it stays terminal, so a real script failure fails fast, never a 180s hang.
          // Only a script_error the script itself sent -- not one the parser synthesized from
          // an unknown type or bad data, which stays terminal as before (spec slipsync-ui §11).
          // A spacing-deferred inject counts as scheduled too (P2-4).
          if (parsed.type === 'script_error' && detail && detail.type === 'script_error' && (urlChangeTimerId || spacingTimerId)) {
            scriptErrorRetryPending = true;
            note('retry_script_error');
            log('message_retry', { type: parsed.type, error: parsed.error });
            return;
          }

          // All other types: remove closeEvent FIRST, close WebView, resolve. The read itself ended, so an earlier
          // not-signed-in answer did not cause this exit: clear it (an exit carries only the answer that caused it).
          lastAnswer = null;
          closing = true;
          note(parsed.type === 'betslips' || parsed.type === 'partial' ? 'success'
            : parsed.type === 'api_error' && parsed.error === 'not_available_in_state' ? 'not_available'
              : parsed.type === 'api_error' ? 'book_error' : 'script_error');
          try { await closeHandle.remove(); } catch (_) {}
          try { await InAppBrowser.close(); } catch (_) {}
          await cleanup();
          const { nonce: _bound, ...boundDetail } = detail; // the nonce stays in the bridge
          done({ type: 'message', detail: boundDetail });
        });
        handles.push(messageHandle);

        // Timeouts: capture keeps its fixed 600 s window (a timeout with pages still delivers them); import books
        // close after INACTIVITY_MS without a page change or at HARD_CAP_MS (bridge hardening).
        onTimeout = async () => {
          if (resolved || closing) return;
          closing = true;
          note('timeout');
          log('timeout');
          try { await closeHandle.remove(); } catch (_) {}
          try { await InAppBrowser.close(); } catch (_) {}
          await cleanup();
          if (isCapture && capturedPages.length > 0) { done({ type: 'message', detail: capturedDetail() }); return; }
          done({ type: 'timeout' });
        };
        if (isCapture) timeoutId = setTimeout(onTimeout, CAPTURE_TIMEOUT_MS);
        else { hardCapId = setTimeout(onTimeout, HARD_CAP_MS); touch(); }

        openedUrl = config.getWebViewUrl(region);
        log('open', { url: redactUrl(openedUrl) });
        // Open WebView
        const openOpts = {
          url: config.getWebViewUrl(region),
          toolbarType: 'compact',
          showArrow: true,
          title: config.webViewTitle,
        };
        // #885: preShowScript requires isPresentAfterPageLoad (plugin InAppBrowserPlugin.swift:780);
        // documentStart installs it as a WKUserScript and reloads so the first load is covered
        // (WKWebViewController.swift:1992-2013). The script itself is idempotent.
        if (isCapture) {
          openOpts.isPresentAfterPageLoad = true;
          openOpts.preShowScript = config.getCaptureScript(nonce);
          openOpts.preShowScriptInjectionTime = 'documentStart';
        }
        // A cancel() (an account change) can land while the listeners above register: open nothing, and remove the
        // listeners registered after cancel's own cleanup ran.
        if (resolved || closing) { await cleanup(); return; }
        await InAppBrowser.openWebView(openOpts);
      } catch (e) {
        const openErr = String(e.message || e).replace(/[^\x20-\x7E]/g, '').slice(0, 200);
        log('open_error', { error: openErr });
        closing = true;
        note('open_error');
        Sentry.captureMessage(`SlipSync openWebView failed: ${openErr}`, 'warning');
        await cleanup();
        done({ type: 'open_error', error: openErr });
      }
    };

    registerAndOpen();
  });

  return { promise, cancel };
}
